import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Activity, Clock, Loader2, TrendingUp, Target, Lightbulb, 
  AlertCircle, RefreshCw, FileText, ChevronRight, Bookmark, Share2,
  Download, FileSpreadsheet
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Skeleton, SkeletonList } from "../components/ui/skeleton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Intelligence() {
  const { darkMode, region, brand } = useOutletContext() || { darkMode: true, region: 'UAE', brand: 'blackswan' };
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningAnalysis, setRunningAnalysis] = useState(false);

  const fetchReports = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/intelligence/reports?region=${region}&brand=${brand}`);
      setReports(res.data);
      if (res.data.length > 0) {
        const detailRes = await axios.get(`${API}/intelligence/reports/${res.data[0].id}`);
        setSelectedReport(detailRes.data);
      } else {
        setSelectedReport(null);
      }
    } catch (error) {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [region, brand]);

  const selectReport = async (reportId) => {
    try {
      const res = await axios.get(`${API}/intelligence/reports/${reportId}`);
      setSelectedReport(res.data);
    } catch (error) {
      toast.error("Failed to load report");
    }
  };

  useEffect(() => { setLoading(true); fetchReports(); }, [fetchReports, region, brand]);

  const triggerRun = async () => {
    setRunningAnalysis(true);
    try {
      const res = await axios.post(`${API}/intelligence/run`, { region, brand });
      toast.success(`Scan started for ${region}`);
      const pollInterval = setInterval(async () => {
        const r = await axios.get(`${API}/intelligence/reports/${res.data.report_id}`);
        if (r.data.status === "completed" || r.data.status === "failed") {
          clearInterval(pollInterval);
          setRunningAnalysis(false);
          fetchReports();
          setSelectedReport(r.data);
        }
      }, 5000);
    } catch (error) {
      setRunningAnalysis(false);
      toast.error("Failed to start scan");
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto space-y-6" data-testid="intelligence-loading">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between animate-fadeIn">
          <div>
            <Skeleton className={`h-8 w-52 mb-2 ${darkMode ? 'bg-[#21262d]' : ''}`} />
            <Skeleton className={`h-4 w-64 ${darkMode ? 'bg-[#21262d]' : ''}`} />
          </div>
          <Skeleton className={`h-10 w-32 rounded-lg ${darkMode ? 'bg-[#21262d]' : ''}`} />
        </div>
        {/* Content Grid Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Reports List */}
          <div className={`lg:col-span-1 rounded-xl overflow-hidden animate-fadeIn stagger-1 ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
            <div className={`px-4 py-3 border-b ${darkMode ? 'border-[#21262d]' : 'border-gray-100'}`}>
              <Skeleton className={`h-4 w-16 ${darkMode ? 'bg-[#21262d]' : ''}`} />
            </div>
            <div className="p-2 space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className={`h-20 w-full rounded-lg ${darkMode ? 'bg-[#21262d]' : ''}`} />
              ))}
            </div>
          </div>
          {/* Report Details */}
          <div className={`lg:col-span-3 rounded-xl overflow-hidden animate-fadeIn stagger-2 ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
            <div className={`px-4 py-3 border-b ${darkMode ? 'border-[#21262d]' : 'border-gray-100'}`}>
              <Skeleton className={`h-9 w-80 rounded-lg ${darkMode ? 'bg-[#21262d]' : ''}`} />
            </div>
            <div className="p-4">
              <SkeletonList count={4} darkMode={darkMode} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="intelligence-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Intelligence Reports</h1>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Automated competitive analysis</p>
        </div>
        <Button onClick={triggerRun} disabled={runningAnalysis} className="bg-[#DC143C] hover:bg-[#b91031] text-white" data-testid="run-analysis-btn">
          {runningAnalysis ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Scanning...</> : <><RefreshCw className="w-4 h-4 mr-2" />New Scan</>}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Reports List */}
        <div className={`lg:col-span-1 rounded-xl overflow-hidden ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
          <div className={`px-4 py-3 border-b ${darkMode ? 'border-[#21262d]' : 'border-gray-100'}`}>
            <h3 className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>Reports</h3>
          </div>
          <ScrollArea className="h-[600px]">
            <div className="p-2">
              {reports.length === 0 ? (
                <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <Activity className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No reports yet</p>
                </div>
              ) : (
                reports.map((r, i) => (
                  <button key={r.id} onClick={() => selectReport(r.id)} data-testid={`report-item-${i}`}
                    className={`w-full text-left p-3 rounded-lg mb-1 transition-colors ${
                      selectedReport?.id === r.id 
                        ? 'bg-[#DC143C]/10 border border-[#DC143C]/20' 
                        : darkMode ? 'hover:bg-[#21262d]' : 'hover:bg-gray-50'
                    }`}>
                    <div className="flex items-center justify-between mb-1">
                      <Badge className={`text-xs ${
                        r.status === 'completed' ? 'bg-emerald-100 text-emerald-700 border-0' :
                        r.status === 'running' ? 'bg-blue-100 text-blue-700 border-0' :
                        'bg-amber-100 text-amber-700 border-0'
                      }`}>{r.status}</Badge>
                      <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{r.run_type}</span>
                    </div>
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {new Date(r.started_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                    <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {r.competitor_count} competitors
                    </p>
                  </button>
                ))
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Report Details */}
        <div className={`lg:col-span-3 rounded-xl overflow-hidden ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
          {selectedReport ? (
            <Tabs defaultValue="insights" className="h-full">
              <div className={`px-4 py-3 border-b flex items-center justify-between ${darkMode ? 'border-[#21262d]' : 'border-gray-100'}`}>
                <TabsList className={darkMode ? 'bg-[#21262d]' : 'bg-gray-100'}>
                  <TabsTrigger value="insights" className="data-[state=active]:bg-[#DC143C] data-[state=active]:text-white">Insights</TabsTrigger>
                  <TabsTrigger value="summary" className="data-[state=active]:bg-[#DC143C] data-[state=active]:text-white">Summary</TabsTrigger>
                  <TabsTrigger value="data" className="data-[state=active]:bg-[#DC143C] data-[state=active]:text-white">Raw Data</TabsTrigger>
                </TabsList>
                {selectedReport.status === "completed" && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className={`gap-2 ${darkMode ? 'border-[#30363d] text-gray-300 hover:bg-[#21262d]' : ''}`}
                        data-testid="export-report-dropdown"
                      >
                        <Download className="w-4 h-4" /> Export
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
                      <DropdownMenuItem
                        onClick={() => window.open(`${API}/export/intelligence-report/${selectedReport.id}/pdf`, '_blank')}
                        className={darkMode ? 'text-gray-300 focus:bg-[#21262d]' : ''}
                        data-testid="export-report-pdf"
                      >
                        <FileText className="w-4 h-4 mr-2" /> Download PDF
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => window.open(`${API}/export/intelligence-report/${selectedReport.id}/csv`, '_blank')}
                        className={darkMode ? 'text-gray-300 focus:bg-[#21262d]' : ''}
                        data-testid="export-report-csv"
                      >
                        <FileSpreadsheet className="w-4 h-4 mr-2" /> Download CSV
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>

              <TabsContent value="insights" className="p-4 m-0">
                <ScrollArea className="h-[550px]">
                  {selectedReport.top_insights?.length > 0 ? (
                    <div className="space-y-3">
                      {selectedReport.top_insights.map((insight, i) => (
                        <div key={insight.id || insight.topic || `insight-${i}`} className={`p-4 rounded-xl ${darkMode ? 'bg-[#21262d] hover:bg-[#30363d]' : 'bg-gray-50 hover:bg-gray-100'} transition-colors`} data-testid={`insight-${i}`}>
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded bg-[#DC143C]/10 flex items-center justify-center text-xs font-semibold text-[#DC143C]">{i + 1}</div>
                              <Badge className="bg-[#DC143C]/10 text-[#DC143C] border-0 text-xs">{insight.topic_category}</Badge>
                            </div>
                            <div className="flex gap-1">
                              <button className={`p-1.5 rounded ${darkMode ? 'hover:bg-[#30363d] text-gray-400' : 'hover:bg-gray-200 text-gray-400'}`}><Bookmark className="w-4 h-4" /></button>
                              <button className={`p-1.5 rounded ${darkMode ? 'hover:bg-[#30363d] text-gray-400' : 'hover:bg-gray-200 text-gray-400'}`}><Share2 className="w-4 h-4" /></button>
                            </div>
                          </div>
                          <h3 className={`font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{insight.topic}</h3>
                          <div className="grid grid-cols-2 gap-4 mb-3">
                            <div className="flex items-start gap-2">
                              <Target className={`w-4 h-4 mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                              <div>
                                <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Audience</p>
                                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{insight.audience_intent || 'N/A'}</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <Lightbulb className={`w-4 h-4 mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                              <div>
                                <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Trigger</p>
                                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{insight.emotional_trigger || 'N/A'}</p>
                              </div>
                            </div>
                          </div>
                          {insight.reason_for_traction && <p className={`text-sm mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{insight.reason_for_traction}</p>}
                          {insight.business_implication && (
                            <div className={`p-3 rounded-lg ${darkMode ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50'}`}>
                              <p className="text-xs text-emerald-600 font-medium mb-1">Opportunity</p>
                              <p className={`text-sm ${darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>{insight.business_implication}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={`p-12 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No insights available</p>
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>

              <TabsContent value="summary" className="p-4 m-0">
                <div className={`p-5 rounded-xl ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                  <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Market Analysis Summary</h3>
                  <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {selectedReport.analysis_summary || 'No summary available'}
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="data" className="p-4 m-0">
                <ScrollArea className="h-[550px]">
                  {selectedReport.scraped_content?.map((item, i) => (
                    <div key={item.url || `scraped-${i}`} className={`p-3 mb-2 rounded-lg ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                      <div className="flex justify-between mb-1">
                        <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{item.competitor_name}</span>
                        <Badge className={item.success ? 'bg-emerald-100 text-emerald-700 border-0' : 'bg-red-100 text-red-700 border-0'}>{item.success ? 'OK' : 'Failed'}</Badge>
                      </div>
                      <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#DC143C] hover:underline truncate block">{item.url}</a>
                    </div>
                  ))}
                </ScrollArea>
              </TabsContent>
            </Tabs>
          ) : (
            <div className={`p-16 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Select a report to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
